<template>
  <div>
    <template v-if="haveRoutes">
      <div v-if="clientRoutes.length > 0" id="clientRouteDetails">
        <expired-route-filter
          :filters="displayExpiredFilters"
          @expire-filter-clicked="expiredFilterClicked"
        />
        <template v-for="(route, index) in clientRoutes">
          <v-expansion-panel
            v-show="isShown(route._id)"
            id="routeDetailsExpansion"
            :key="route.id"
            v-model="expansionPanelValue[index]"
            :class="
              isPastRoute(route) || isDeclined(route) ? 'expired' : route.state
            "
            :value="expansionPanelValue[index]"
          >
            <v-expansion-panel-content id="expansionContent">
              <v-icon slot="actions">
                keyboard_arrow_down
              </v-icon>
              <div slot="header" class="panel-header">
                <v-layout wrap align-center>
                  <v-flex xs6 sm3 class="pr-2">
                    {{ route.name }}
                  </v-flex>
                  <v-flex xs12 sm6 md3>
                    {{ route.startTime | dateFormat }}
                  </v-flex>
                  <v-flex
                    xs6
                    sm3
                    md2
                    :class="[getRouteState(route), 'uppercase']"
                  >
                    {{ getRouteState(route) }}
                  </v-flex>
                  <v-flex id="panelButtonContainer" md4>
                    <v-btn
                      v-for="element in generateButtons(route)"
                      :key="element.name"
                      :hidden="isPastRoute(route)"
                      :outline="element.outline"
                      :color="element.color"
                      :class="element.class"
                      small
                      round
                      dark
                      @click.stop="element.callback"
                    >
                      {{ element.name }}
                    </v-btn>
                  </v-flex>
                </v-layout>
              </div>
              <v-card>
                <route-details-modal
                  v-if="expansionPanelValue[index] === 0"
                  :route-details="clientRoutes[index]"
                  :hide-actions="true"
                  :show-top-content="false"
                />
                <div v-else style="height: 20rem" />
              </v-card>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </template>
        <v-dialog
          v-model="confirmationModal.status"
          :width="alert.width"
          :max-width="alert.maxWidth"
        >
          <confirmation-modal
            :modal-options="confirmationModal"
            :modal-icon="'info'"
            @on-negative-clicked="closeShowingConfirmation"
            @on-positive-clicked="cancel ? cancelRoute() : requestConfirmed()"
          />
        </v-dialog>
      </div>
      <preloader
        v-else
        loading-message="Loading your routes and getting property details..."
      />
    </template>
    <div v-else>
      <title-with-message
        title="No Routes for this client"
        message="Click on Request Home Tab above or button below to start creating a new route"
        btn-option="Create a Route"
        :content-small="true"
        @button-clicked="$emit('go-to-requested-homes')"
      />
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { mapState, mapMutations } from 'vuex'
import Preloader from '../../uiUtils/Preloader.vue'
import ConfirmationModal from '../../modals/ConfirmationModal.vue'
import ExpiredRouteFilter from './ExpiredRouteFilter.vue'
import TitleWithMessage from '../../uiUtils/TitleWithMessage.vue'
import RouteDetailsModal from '../../modals/RouteDetailsModal.vue'
import routeDataContent from '../../mixins/routeDataContent'
import {
  isDeclinedRoute,
  isNotScheduledType,
  isSendReminderActive,
  isCancelledRoute
} from '../../../utils/routeUtils'
import { sendRouteSchedule } from '../../../services/messagesService'
import colors from '../../../scss/_colors.scss'
import {
  requestRouteShowing,
  updateSavedRoutesOnPendingRequest,
  sendScheduleRequest,
  cancelDeleteRoute
} from '../../../services/routeService'
import { isEmpty } from '../../../utils/generalUtils'

export default {
  filters: {
    dateFormat: value => {
      return moment(value).format('MM-DD-YY hh:mma')
    }
  },

  components: {
    Preloader,
    ConfirmationModal,
    RouteDetailsModal,
    TitleWithMessage,
    ExpiredRouteFilter
  },

  mixins: [routeDataContent],

  props: {
    clientRoutes: {
      type: Array,
      required: true
    },

    haveRoutes: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      confirmationModal: {
        status: false,
        message: null,
        positive: null,
        negative: null
      },
      selectedRoute: null,
      displayExpiredFilters: [
        { name: 'show expired', value: 'show' },
        { name: 'hide expired', value: 'hide' }
      ],
      selectedFilter: 'show',
      expansionPanelValue: [],
      cancel: false
    }
  },

  computed: {
    ...mapState('style', ['alert']),
    ...mapState('agent', ['agentMessages', 'resetExpansionPanels'])
  },

  watch: {
    clientRoutes: {
      handler: function(value) {
        if (this.resetExpansionPanels) {
          this.expansionPanelValue = value.map(item => {
            return null
          })
          this.setResetExpansionPanels(false)
        }
      },
      deep: true
    }
  },

  mounted() {
    this.expansionPanelValue = this.clientRoutes.map(item => {
      return null
    })
  },

  methods: {
    ...mapMutations('home', {
      setActiveRoute: 'SET_ACTIVE_ROUTE'
    }),
    ...mapMutations('common', {
      setSelectedRoute: 'SET_SELECTED_ROUTE',
      setSelectedRouteLoading: 'SET_SELECTED_ROUTE_LOADING'
    }),
    ...mapMutations('agent', {
      setResetExpansionPanels: 'SET_RESET_EXPANSION_PANELS',
      setRht: 'SET_RHT'
    }),

    isShown(id) {
      if (this.selectedFilter === 'hide') {
        return true
      }
      const ids = this.clientRoutes
        .filter(
          route =>
            !isDeclinedRoute(route) &&
            !isNotScheduledType(route) &&
            !this.isPastRoute(route)
        )
        .map(item => item._id)
      return !!ids.includes(id)
    },

    isDeclined(routeData) {
      return isDeclinedRoute(routeData) || isNotScheduledType(routeData)
    },

    getRouteState(routeData) {
      if (isCancelledRoute(routeData)) {
        return 'expired - cancelled'
      } else if (isDeclinedRoute(routeData)) {
        return 'expired - declined'
      } else if (isNotScheduledType(routeData)) {
        return 'expired - unscheduled'
      } else {
        return this.isPastRoute(routeData)
          ? `expired - ${routeData.state.toLowerCase()}`
          : routeData.state
      }
    },

    generateButtons(routeData) {
      const { state } = routeData
      if (isDeclinedRoute(routeData) || isNotScheduledType(routeData)) {
        return []
      }
      switch (state) {
        case 'saved':
          return [
            {
              name: 'revise route',
              outline: true,
              color: colors.hwayzBlue,
              callback: () => this.reviseSavedRoute(routeData)
            },
            {
              name: 'request showing',
              color: colors.pending,
              callback: () => this.openShowingConfirmation(routeData)
            }
          ]
        case 'confirmed':
          return [
            {
              name: 'send schedule',
              color: colors.saved,
              callback: () => this.sendSchedule(routeData)
            },
            {
              name: 'cancel route',
              outline: true,
              color: colors.secondary,
              callback: () => this.openCancelConfirmation(routeData)
            }
          ]
        case 'scheduled':
          let returnValue = isSendReminderActive(routeData)
            ? [
              {
                name: 'sent',
                color: colors.scheduled,
                outline: true,
                class: 'disable-element',
                callback: () => this.sendSchedule(routeData, true)
              }
            ]
            : [
              {
                name: 'send reminder',
                color: colors.scheduled,
                callback: () => this.sendSchedule(routeData, true)
              }
            ]
          returnValue.push({
            name: 'cancel route',
            outline: true,
            color: colors.secondary,
            callback: () => this.openCancelConfirmation(routeData)
          })
          return returnValue
        default:
          return []
      }
    },

    reviseSavedRoute(routeForRevision) {
      this.setSelectedRoute(routeForRevision)
      this.setSelectedRouteLoading(true)
      this.$router.push({ name: 'plan-route' })
      this.setActiveRoute('plan-route')
    },

    openShowingConfirmation(routeData) {
      this.selectedRoute = { ...routeData }
      Object.assign(this.confirmationModal, {
        status: true,
        message: `Make sure you have confirmed your entries for this route. Do you want to request showings for properties in this route?`,
        positive: 'Yes, send request',
        negative: 'No, not now'
      })
    },

    openCancelConfirmation(routeData) {
      this.selectedRoute = { ...routeData }
      this.confirmationModal = {
        ...this.confirmationModal,
        status: true,
        message: `Are you sure you want to cancel route ${routeData.name}?`,
        positive: 'Yes, cancel route',
        negative: 'No'
      }
      this.cancel = true
    },

    closeShowingConfirmation() {
      this.selectedRoute = null
      this.cancel = false
      Object.assign(this.confirmationModal, {
        status: false,
        message: null
      })
    },

    requestConfirmed() {
      this.requestShowing()
      this.closeShowingConfirmation()
    },

    // Function to delete/cancel a route
    async cancelRoute() {
      const responseObject = await cancelDeleteRoute(this.selectedRoute)
      if (!isEmpty(responseObject)) {
        const { isSaved, data } = responseObject
        if (!isSaved) {
          this.updateRhtAfterCancelRoute(data)
          this.updateAgentRoutesOnCancel(data)
        } else {
          this.setAgentRouteData(
            this.agentRouteData.filter(route => route._id !== data._id)
          )
        }
        this.updateClientDataOnCancel(this.clientData, data, isSaved)
      }
      this.closeShowingConfirmation()
    },

    async requestShowing() {
      const response = await requestRouteShowing(this.selectedRoute)

      if (response.status === 200) {
        updateSavedRoutesOnPendingRequest(this.clientData, response.data)
        this.routesUpdateAfterActions(
          response.data,
          this.clientData,
          this.agentRouteData
        )
        this.updateRhtAfterRequestShowing(response.data)
      } else if (response.state === 409) {
        console.log('conflict occurred')
      } else {
        console.log('some error occurred')
      }
    },

    async sendSchedule(data, isReminder = false) {
      const response = await sendScheduleRequest(data)
      if (response.status === 200) {
        let updatedRoute = { ...response.data }
        // if (isReminder) {
        //   updatedRoute = { ...updatedRoute, lastMsgDuration: 0 }
        // }
        this.routesUpdateAfterActions(
          updatedRoute,
          this.clientData,
          this.agentRouteData
        )

        const subject = isReminder ? 'Schedule Reminder' : `Scheduled Route`

        return sendRouteSchedule({
          to: [data.clientId],
          message: 'Route Details',
          date: moment(data.startTime).format('LLLL'),
          subject,
          routeId: data._id
        })
      } else {
        console.log('some error occurred')
      }
    },

    /**
     * sets the filter type to display expired routes or not based on selection
     */
    expiredFilterClicked(filter) {
      this.selectedFilter = filter.value
    }
  }
}
</script>

<style lang="sass">
#map
  height: auto !important

.unscheduled-table
  margin-top: 10px
  h2
    margin-bottom: 0px
@mixin expansion-border($color)
  .v-expansion-panel__header
    border-color: $color
  .v-expansion-panel__body
    border-color: $color

#routeDetailsExpansion
  border-radius: 1.5rem
  box-shadow: none !important
  #expansionContent
    border-radius: 1.5rem
    font-size: $hwayzFont1rem3
    margin-bottom: 1.5rem !important

.client-route-details__map
  min-height: 35rem
  display: flex
  flex-wrap: wrap
  justify-content: center
  width: 100%
  padding: 1rem

.panel-header
  align-items: center

.progress-spinner
  padding-top: 8rem
  text-align: center

#panelButtonContainer
  text-align: right
  .v-btn
    height: 28px
    font-size: $hwayzFont1rem
    margin: 0 2rem 0 0

#clientRouteDetails
  .v-expansion-panel__header
    border-radius: 1.5rem 1.5rem 0rem 0rem
    border-style: solid
    border-width: 0.2rem
  .v-expansion-panel__body
    border-radius: 0rem 0rem 1.5rem 1.5rem
    border-style: solid
    border-width: 0.2rem
    border-top: none
  .v-card
    border-radius: 0rem 0rem 1.5rem 1.5rem

  // Defining state color classes
  .saved
    @include expansion-border($saved)
  .pending
    @include expansion-border($hwayzYellow)
  .scheduled
    @include expansion-border($hwayzGreen)
  .confirmed
    @include expansion-border($hwayzBlue)
  .expired
    @include expansion-border($color-grey)
</style>
